<script setup lang="ts">
import type { TournamentRound } from "@interfaces/tournament_type";
import { useI18n } from "vue-i18n";

interface Props {
  tournamentRounds: TournamentRound[];
  currentRound: number;
  isTournamentStarted: boolean;
  isTournamentFinished: boolean;
  canAdvance: boolean;
  actionLoading: boolean;
}

interface Emits {
  (e: 'advance-to-next-round'): void;
}

defineProps<Props>();
defineEmits<Emits>();

const { t } = useI18n();
</script>

<template>
  <!-- Tournament Overview -->
  <div
    v-if="tournamentRounds && tournamentRounds.length > 0"
    class="tournament-overview"
  >
    <h4 class="overview-title">{{ t("tournament.overview.title") }}</h4>
    <div class="rounds-overview">
      <div
        v-for="round in tournamentRounds"
        :key="round.id"
        class="round-overview-item"
        :class="{
          'current-round': round.round_number === currentRound && !isTournamentFinished,
          'finished-round': isTournamentFinished,
        }"
      >
        <div class="round-number">
          {{ t("tournament.round") }} {{ round.round_number }}
          <span v-if="isTournamentFinished" class="tournament-finished-badge">🏆 {{ t("tournament.status.finished") }}</span>
        </div>
        <div class="round-info">
          <div v-if="round.map" class="round-map-info">
            <img
              v-if="round.map.image_url"
              :src="round.map.image_url"
              :alt="round.map.name"
              class="map-thumbnail-small"
            />
            <span class="map-name">{{ round.map.name }}</span>
          </div>
          <div v-if="round.scenario" class="round-scenario-info">
            <span class="scenario-name">{{ round.scenario.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Duplicate Finish Round Button -->
    <div class="round-finish-container">
      <button
        v-if="isTournamentStarted && canAdvance && !isTournamentFinished"
        @click="$emit('advance-to-next-round')"
        class="btn btn-round-advance"
        type="button"
        :disabled="actionLoading"
        :title="t('tournament.overview.advance_round_title')"
      >
        {{ actionLoading ? t("tournament.overview.advancing") : t("tournament.overview.advance_round") }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tournament-overview {
  background-color: white;
  border-radius: 6px;
  border: 1px solid #bd9c65;
  overflow: hidden;
  margin-top: 20px;
}

.overview-title {
  background-color: #c29c58;
  color: #60451f;
  margin: 0;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.rounds-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  padding: 15px;
}

.round-overview-item {
  border: 1px solid #bd9c65;
  border-radius: 6px;
  padding: 12px;
  background-color: #fafafa;
  transition: all 0.2s ease;

  &.current-round {
    background-color: #e8f5e8;
    border-color: #4caf50;
    transform: scale(1.02);
  }

  &.finished-round {
    background-color: #f0f8ff;
    border-color: #2196f3;
    opacity: 0.8;
  }
}


.round-number {
  font-weight: bold;
  color: #60451f;
  font-size: 16px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tournament-finished-badge {
  background-color: #4caf50;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.round-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.round-map-info,
.round-scenario-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.map-thumbnail-small {
  width: 30px;
  height: 22px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid #bd9c65;
}

.map-name,
.scenario-name {
  color: #666;
  font-weight: 500;
}

.round-finish-container {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);
}

.btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-md);
  font-weight: 600;
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: var(--transition-normal);
  border: none;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  text-decoration: none;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.btn-round-advance {
    background: var(--gradient-success);
    color: var(--light-base);
    border: 2px solid var(--success-color);

    &:hover:not(:disabled) {
      background: var(--gradient-success-hover);
      border-color: var(--success-hover);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px var(--shadow-light);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 2px 6px var(--shadow-light);
    }
  }
}

.round-advance-tooltip {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  background: var(--gradient-secondary);
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  max-width: 300px;

  i {
    color: var(--icon-color);
    font-size: 14px;
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .tournament-overview {
    padding: var(--spacing-md);
  }

  .rounds-overview {
    grid-template-columns: 1fr;
    gap: var(--spacing-sm);
  }

  .round-overview-item {
    padding: var(--spacing-sm);
  }

  .round-number {
    font-size: var(--font-size-md);
  }

  .round-map-info,
  .round-scenario-info {
    font-size: var(--font-size-xs);
  }

  .map-thumbnail-small {
    width: 30px;
    height: 21px;
  }

  .round-finish-container {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }

  .round-advance-tooltip {
    text-align: center;
    font-size: 11px;
    padding: 6px 8px;
  }
}

@media (max-width: 480px) {
  .tournament-overview {
    padding: var(--spacing-sm);
  }

  .btn {
    padding: 10px 14px;
    font-size: 14px;
  }
}
</style>
